import { AssessmentStatus } from "api/generated/schemas/assessmentStatus";
import { AssignmentStatus } from "api/generated/schemas/assignmentStatus";
import { infoIcon, toClassicIcon } from "common/defaultIcons";

export const ASSIGNMENT_STATUS_COLOR: Record<AssignmentStatus, string> = {
  [AssignmentStatus.open]: "portfolio-concept",
  [AssignmentStatus.overdue]: "warning",
  [AssignmentStatus.completed]: "positive",
  [AssignmentStatus.completed_late]: "negative",
  [AssignmentStatus.draft]: "portfolio-concept",
};

export const ASSIGNMENT_STATUS_LABEL: Record<AssignmentStatus, string> = {
  [AssignmentStatus.open]: "Open",
  [AssignmentStatus.overdue]: "Te laat",
  [AssignmentStatus.completed]: "Afgerond",
  [AssignmentStatus.completed_late]: "Te laat afgerond",
  [AssignmentStatus.draft]: "Concept",
};

/**
 * Labels of the actions a student can take on an assignment, keyed by action
 * rather than by status: the status bar picks the action from the backend
 * permissions (`meta.allowCreate` / `allowEdit` / `allowRetry`), not from the
 * assignment status alone.
 */
export const ASSIGNMENT_ACTION_LABEL = {
  view: "Taak bekijken",
  create: "Taak uitvoeren",
  edit: "Taak bewerken",
  submit: "Taak inleveren",
} as const;

export const ASSIGNMENT_DEADLINE_PASSED_LABEL = "Deadline verstreken";

export const ASSIGNMENT_STATUS_CTA_LABEL: Record<
  AssignmentStatus,
  string | null
> = {
  [AssignmentStatus.open]: ASSIGNMENT_ACTION_LABEL.create,
  [AssignmentStatus.overdue]: ASSIGNMENT_ACTION_LABEL.create,
  [AssignmentStatus.completed]: null,
  [AssignmentStatus.completed_late]: null,
  [AssignmentStatus.draft]: ASSIGNMENT_ACTION_LABEL.edit,
};

export const ASSIGNMENT_OVERVIEW_ROUTE_BY_STATUS: Record<
  AssignmentStatus,
  string
> = {
  [AssignmentStatus.open]: "studentOpenAssignments",
  [AssignmentStatus.overdue]: "studentOpenAssignments",
  [AssignmentStatus.completed]: "studentCompletedAssignments",
  [AssignmentStatus.completed_late]: "studentCompletedAssignments",
  [AssignmentStatus.draft]: "studentOpenAssignments",
};

/**
 * Returns the student overview tab route an assignment belongs to.
 *
 * Mirrors the backend categorization in PortfolioStudentAssignmentController:
 * - awaiting tab: completed AND assessment_status = pending_review
 * - open tab:     not completed OR assessment_status = changes_requested
 * - completed tab: completed AND assessment_status not in [pending_review, changes_requested]
 *
 * Without this, an assignment that's `completed` but still pending review would
 * incorrectly route to the Completed tab instead of "Wacht op beoordeling".
 */
export const getAssignmentOverviewRoute = (
  status: AssignmentStatus | undefined,
  assessmentStatus: AssessmentStatus | null | undefined,
): string => {
  if (assessmentStatus === AssessmentStatus.pending_review) {
    return "studentAwaitingAssessmentAssignments";
  }

  if (assessmentStatus === AssessmentStatus.changes_requested) {
    return "studentOpenAssignments";
  }

  if (status === undefined) {
    return "studentOpenAssignments";
  }

  return ASSIGNMENT_OVERVIEW_ROUTE_BY_STATUS[status];
};

export const ASSIGNMENT_STATUS_BAR_LABEL: Record<AssignmentStatus, string> = {
  [AssignmentStatus.open]:
    "Je hebt nog {days}, {hours} en {minutes} om deze taak af te ronden",
  [AssignmentStatus.overdue]:
    "De deadline van deze taak is verstreken, maar je kan de taak nog wel uitvoeren",
  // {submission} is "Ingeleverd op: ..." or, for a task that is never
  // assessed, "Afgerond op: ..." -- see getSubmissionDateLabel().
  [AssignmentStatus.completed]: "{submission}",
  [AssignmentStatus.completed_late]: "Te laat — {submission}",
  [AssignmentStatus.draft]:
    "Je hebt nog {days}, {hours} en {minutes} om deze taak af te ronden",
};

export const ASSIGNMENT_STATUS_BAR_ICON: Record<AssignmentStatus, string> = {
  [AssignmentStatus.open]: toClassicIcon(infoIcon, "solid"),
  [AssignmentStatus.overdue]: toClassicIcon(infoIcon, "solid"),
  [AssignmentStatus.completed]: toClassicIcon("fa-check-circle", "solid"),
  [AssignmentStatus.completed_late]: toClassicIcon("fa-check-circle", "solid"),
  [AssignmentStatus.draft]: toClassicIcon(infoIcon, "solid"),
};

export const ASSIGNMENT_STATUS_BAR_ICON_COLOR: Record<
  AssignmentStatus,
  string
> = {
  [AssignmentStatus.open]: "info",
  [AssignmentStatus.overdue]: "info",
  [AssignmentStatus.completed]: "positive",
  [AssignmentStatus.completed_late]: "positive",
  [AssignmentStatus.draft]: "info",
};
